import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Quote Estimator', path: '/quote-estimator', highlight: true },
    { name: 'First Timer Zone', path: '/first-timer' },
    { name: 'Healing Tracker', path: '/healing-tracker' },
    { name: 'F&T Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const MobileMenu = () => (
    <AnimatePresence>
      {isMenuOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 bg-black/50"
            style={{ zIndex: 99998 }}
            onClick={() => setIsMenuOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="md:hidden fixed top-0 right-0 bottom-0 w-[280px] bg-[#F8F8F5] shadow-2xl flex flex-col"
            style={{ zIndex: 99999 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between p-4 border-b border-black/10">
              <span className="font-heading text-lg font-medium">Menu</span>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 hover:bg-black/5 rounded-full transition-colors"
                data-testid="mobile-menu-close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex flex-col p-4 gap-1 flex-1 overflow-y-auto">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`font-mono text-sm uppercase tracking-widest py-3 px-4 rounded-lg transition-colors ${
                    location.pathname === link.path 
                      ? 'bg-black text-white' 
                      : 'hover:bg-black/5'
                  } ${link.highlight ? 'font-black' : ''}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="p-4 border-t border-black/10">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-2"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_c2d28a08-ae0d-49cc-b72b-7be69e3b538c/artifacts/daj5mwbp_IMG_3269.png"
                  alt="Vision Tattoo Logo"
                  className="h-16 w-auto object-contain mix-blend-multiply"
                />
                <span className="font-heading text-sm">Vision Tattoo</span>
              </Link>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  const HeaderContent = () => (
    <header 
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 bg-[#F8F8F5] shadow-sm"
      style={{ zIndex: 99990 }}
    >
      <nav className="px-6 md:px-12 lg:px-24 py-3 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 sm:gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_c2d28a08-ae0d-49cc-b72b-7be69e3b538c/artifacts/daj5mwbp_IMG_3269.png"
            alt="Vision Tattoo Logo"
            className="h-24 sm:h-28 md:h-32 w-auto object-contain mix-blend-multiply"
          />
          <span className="font-heading text-base sm:text-lg md:text-xl font-medium tracking-tight">Vision Tattoo</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${
                link.highlight 
                  ? 'font-bold text-black bg-black/5 px-3 py-1.5 rounded' 
                  : 'hover:text-muted-foreground'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>
    </header>
  );

  return (
    <>
      <div className="h-[72px] sm:h-[80px]" />
      {mounted && createPortal(<HeaderContent />, document.body)}
      {mounted && createPortal(<MobileMenu />, document.body)}
    </>
  );
};

export default Header;
